/**
 * UnifiedActionsEditor - Unified action configuration for all co-pilot types
 * 
 * Features:
 * - Groups actions by scope (Item Actions / Collection Actions)
 * - Shows ALL actions regardless of co-pilot type
 * - Disables actions not supported for the entity type (with explanations)
 * - Shows editable fields section for all co-pilots
 * - Works with ACTION_REGISTRY and FIELD_REGISTRY from backend
 */

import React, { useState, useEffect } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  Edit, Plus, Trash2, GitMerge, Scissors, Link, Unlink, 
  RefreshCw, Layers, Link2, ChevronDown, ChevronUp,
  AlertTriangle, Info, ExternalLink, Navigation, FileText, X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../services/api';

// Icon mapping for actions
const ACTION_ICONS = {
  AWAIT_FEEDBACK: RefreshCw,
  UPDATE_ITEM: Edit,
  CREATE_ITEM: Plus,
  LINK_ITEMS: Link,
  UNLINK_ITEMS: Unlink,
  DELETE_ITEM: Trash2,
  MERGE_ITEMS: GitMerge,
  SPLIT_ITEM: Scissors,
  BULK_UPDATE: Layers,
  BULK_CREATE: Plus,
  BULK_LINK: Link2,
  // Navigation actions (for Executive Co-Pilot)
  OPEN_ENTITY: ExternalLink,
  NAVIGATE: Navigation,
  CREATE_FEATURE_REQUEST: FileText,
};

// Tier labels and colors
const TIER_CONFIG = {
  1: { label: "Direct Apply", color: "text-green-600 dark:text-green-400", bgColor: "bg-green-500/10", borderColor: "border-green-500/20" },
  2: { label: "Requires Confirmation", color: "text-amber-600 dark:text-amber-400", bgColor: "bg-amber-500/10", borderColor: "border-amber-500/20" },
  3: { label: "Dangerous", color: "text-red-600 dark:text-red-400", bgColor: "bg-red-500/10", borderColor: "border-red-500/20" },
};

// Scope labels
const SCOPE_CONFIG = {
  item: { 
    label: "Item Actions", 
    description: "Available when working with a single entity",
    icon: Edit,
    bgColor: "bg-blue-500/5",
    borderColor: "border-blue-500/20"
  },
  collection: { 
    label: "Collection Actions", 
    description: "Available when working with lists of entities",
    icon: Layers,
    bgColor: "bg-purple-500/5",
    borderColor: "border-purple-500/20"
  },
  both: { 
    label: "Universal Actions", 
    description: "Available in all contexts",
    icon: RefreshCw,
    bgColor: "bg-gray-500/5",
    borderColor: "border-gray-500/20"
  }
};

/**
 * UnifiedActionsEditor Component
 * 
 * @param {Object} editedPrompt - The co-pilot configuration being edited
 * @param {Object} actionRegistry - Registry of all actions (from backend)
 * @param {Object} fieldRegistry - Registry of all fields (from backend)
 * @param {Function} onChange - Callback when configuration changes
 * @param {Object} colors - Theme colors
 */
export const UnifiedActionsEditor = ({ 
  editedPrompt,
  actionRegistry = {},
  fieldRegistry = {},
  availableDomains = [],  // NEW: List of available domains with entity_type mappings
  onChange,
  colors = {}
}) => {
  const [expandedScopes, setExpandedScopes] = useState({ item: true, collection: true });
  const [expandedFields, setExpandedFields] = useState(true);
  
  // Extract config from prompt
  const promptScope = editedPrompt?.scope || editedPrompt?.category;
  const enabledActions = editedPrompt?.enabled_actions || [];
  const fieldRestrictions = editedPrompt?.field_restrictions || {};
  const unsupportedActions = editedPrompt?.unsupported_actions || [];
  const selectedDomains = editedPrompt?.domains || [];
  
  // Get display name for entity (for "Not for X" labels)
  const entityDisplayName = editedPrompt?.entity_display_name || 'this co-pilot';
  
  // Build domain → entity_type mapping from availableDomains
  const domainToEntityMap = {};
  availableDomains.forEach(d => {
    if (d.entity_type) {
      domainToEntityMap[d.value] = d.entity_type;
    }
  });
  
  // Get entity types from selected domains
  const selectedEntityTypes = selectedDomains
    .map(domain => domainToEntityMap[domain])
    .filter(Boolean);
  
  // Aggregate fields from ALL selected domains
  const aggregatedFields = {};
  selectedEntityTypes.forEach(entityType => {
    const entityFields = fieldRegistry[entityType]?.fields || {};
    Object.entries(entityFields).forEach(([fieldName, fieldConfig]) => {
      // Prefix with entity type to avoid collisions, store source
      const key = `${entityType}.${fieldName}`;
      aggregatedFields[key] = {
        ...fieldConfig,
        sourceEntityType: entityType,
        fieldName: fieldName
      };
    });
  });
  
  // Group actions by scope
  const itemActions = Object.values(actionRegistry)
    .filter(a => a.scope === 'item' || a.scope === 'both')
    .sort((a, b) => a.tier - b.tier);
  
  const collectionActions = Object.values(actionRegistry)
    .filter(a => a.scope === 'collection')
    .sort((a, b) => a.tier - b.tier);
  
  // Check if an action is supported for selected domains
  // An action is supported if ANY of the selected entity types is in supported_entities
  const isActionSupported = (action) => {
    // If no domains selected, all actions are potentially supported
    if (selectedEntityTypes.length === 0) return true;
    
    // Check per-copilot unsupported_actions first
    if (unsupportedActions.includes(action.action_id)) {
      return false;
    }
    
    // Then check action registry supported_entities
    if (!action.supported_entities) return true;
    if (action.supported_entities.includes('all')) return true;
    
    // Check if ANY selected entity type is supported
    return selectedEntityTypes.some(et => action.supported_entities.includes(et));
  };
  
  // Check if action is enabled
  const isActionEnabled = (actionId) => {
    // AWAIT_FEEDBACK is always considered enabled
    if (actionId === 'AWAIT_FEEDBACK') return true;
    return enabledActions.includes(actionId);
  };
  
  // Toggle an action
  const toggleAction = (actionId, enabled) => {
    let newActions;
    if (enabled) {
      newActions = [...new Set([...enabledActions, actionId])];
    } else {
      newActions = enabledActions.filter(id => id !== actionId);
    }
    // Always keep AWAIT_FEEDBACK
    if (!newActions.includes('AWAIT_FEEDBACK')) {
      newActions.push('AWAIT_FEEDBACK');
    }
    onChange('enabled_actions', newActions);
  };
  
  // Toggle an action as unsupported for this co-pilot
  const toggleUnsupported = (actionId, makeUnsupported) => {
    let newUnsupported;
    if (makeUnsupported) {
      newUnsupported = [...new Set([...unsupportedActions, actionId])];
      // Also remove from enabled_actions if it was enabled
      const newEnabled = enabledActions.filter(id => id !== actionId);
      onChange('enabled_actions', newEnabled);
    } else {
      newUnsupported = unsupportedActions.filter(id => id !== actionId);
    }
    onChange('unsupported_actions', newUnsupported);
  };
  
  // Check if action is explicitly marked as unsupported (in unsupported_actions list)
  const isExplicitlyUnsupported = (actionId) => {
    return unsupportedActions.includes(actionId);
  };
  
  // Toggle a field restriction
  // Field key format: "entityType.fieldName"
  // DESTRUCTIVE: Only keeps fields that are visible in the UI (from aggregatedFields)
  // This ensures stale/hidden fields are automatically cleaned up on any toggle
  const toggleField = (fieldKey, enabled) => {
    // Build fresh restrictions from aggregatedFields only
    const newRestrictions = {};
    Object.keys(aggregatedFields).forEach(key => {
      // Preserve existing enabled state for visible fields, or false if not set
      newRestrictions[key] = key === fieldKey ? enabled : (fieldRestrictions[key] === true);
    });
    onChange('field_restrictions', newRestrictions);
  };
  
  // Check if a field is enabled - must be EXPLICITLY true in database
  const isFieldEnabled = (fieldKey) => {
    return fieldRestrictions[fieldKey] === true;
  };
  
  // Enable all actions in a scope
  const enableAllInScope = (scope) => {
    const scopeActions = scope === 'item' ? itemActions : collectionActions;
    const supportedActions = scopeActions
      .filter(a => isActionSupported(a))
      .map(a => a.action_id);
    const newActions = [...new Set([...enabledActions, ...supportedActions])];
    onChange('enabled_actions', newActions);
  };
  
  // Disable all actions in a scope (except AWAIT_FEEDBACK)
  const disableAllInScope = (scope) => {
    const scopeActions = scope === 'item' ? itemActions : collectionActions;
    const scopeIds = scopeActions.map(a => a.action_id);
    const newActions = enabledActions.filter(id => !scopeIds.includes(id) || id === 'AWAIT_FEEDBACK');
    onChange('enabled_actions', newActions);
  };
  
  // Toggle all fields
  // DESTRUCTIVE: Only includes fields from aggregatedFields (visible in UI)
  const toggleAllFields = (enabled) => {
    const newRestrictions = {};
    Object.keys(aggregatedFields).forEach(fieldKey => {
      newRestrictions[fieldKey] = enabled;
    });
    onChange('field_restrictions', newRestrictions);
  };
  
  // Count enabled actions in scope
  const countEnabled = (actions) => {
    return actions.filter(a => isActionEnabled(a.action_id) && isActionSupported(a)).length;
  };
  
  // Render a single action row
  const renderActionRow = (action) => {
    const Icon = ACTION_ICONS[action.action_id] || RefreshCw;
    const enabled = isActionEnabled(action.action_id);
    const supported = isActionSupported(action);
    const tierConfig = TIER_CONFIG[action.tier] || TIER_CONFIG[1];
    const isAwaitFeedback = action.action_id === 'AWAIT_FEEDBACK';
    const explicitlyUnsupported = isExplicitlyUnsupported(action.action_id);
    
    // Check if action is unsupported due to action registry (not explicit override)
    const registryUnsupported = !supported && !explicitlyUnsupported;
    
    return (
      <div 
        key={action.action_id}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg transition-all border",
          !supported && "opacity-40",
          enabled && supported
            ? cn(tierConfig.bgColor, tierConfig.borderColor)
            : "bg-gray-500/5 border-transparent"
        )}
      >
        <Checkbox
          id={`action-${action.action_id}`}
          checked={enabled}
          onCheckedChange={(checked) => toggleAction(action.action_id, checked)}
          disabled={!supported || isAwaitFeedback}
        />
        <div className={cn(
          "p-1.5 rounded shrink-0",
          enabled && supported ? tierConfig.bgColor : "bg-gray-500/10"
        )}>
          <Icon className={cn(
            "w-4 h-4",
            enabled && supported ? tierConfig.color : "text-gray-400"
          )} />
        </div>
        <div className="flex-1 min-w-0">
          <Label 
            htmlFor={`action-${action.action_id}`}
            className={cn(
              "text-sm font-medium cursor-pointer block",
              !supported && "cursor-not-allowed",
              enabled && supported ? colors.textPrimary : colors.textMuted
            )}
          >
            {action.name}
          </Label>
          <p className={cn("text-xs", colors.textMuted)}>
            {action.description}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Show "Not for X" badge for unsupported actions */}
          {!supported && (
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700",
                explicitlyUnsupported 
                  ? "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700" 
                  : "bg-gray-100 dark:bg-gray-800"
              )}
              onClick={() => explicitlyUnsupported && toggleUnsupported(action.action_id, false)}
              title={explicitlyUnsupported ? "Click to re-enable this action" : "Disabled by action registry"}
            >
              Not for {entityDisplayName}
              {explicitlyUnsupported && <X className="w-3 h-3 ml-1" />}
            </Badge>
          )}
          {/* Show disable button for supported actions (except AWAIT_FEEDBACK) */}
          {supported && !isAwaitFeedback && (
            <button
              onClick={() => toggleUnsupported(action.action_id, true)}
              className={cn(
                "text-xs px-2 py-1 rounded border transition-colors",
                "text-gray-500 hover:text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20",
                colors.borderPrimary
              )}
              title={`Mark as unavailable for ${entityDisplayName}`}
            >
              Disable
            </button>
          )}
          {action.requires_confirmation && supported && (
            <Badge variant="outline" className={cn("text-xs", tierConfig.bgColor, tierConfig.color)}>
              Confirm
            </Badge>
          )}
          {action.tier === 3 && supported && (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
        </div>
      </div>
    );
  };
  
  // Render an action group (Item or Collection)
  const renderActionGroup = (scope, actions) => {
    const config = SCOPE_CONFIG[scope];
    const GroupIcon = config.icon;
    const isExpanded = expandedScopes[scope];
    const enabledCount = countEnabled(actions);
    const supportedCount = actions.filter(a => isActionSupported(a)).length;
    
    // Check if this scope matches the prompt's scope for highlighting
    const isActiveScope = promptScope === scope || (promptScope === 'both');
    
    return (
      <Card className={cn(
        "overflow-hidden transition-all",
        colors.bgCard,
        isActiveScope ? config.borderColor : colors.borderPrimary,
        isActiveScope && "border-2"
      )}>
        <Collapsible open={isExpanded} onOpenChange={() => setExpandedScopes(s => ({...s, [scope]: !s[scope]}))}>
          <CollapsibleTrigger asChild>
            <button className={cn(
              "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
              config.bgColor
            )}>
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", config.bgColor, "border", config.borderColor)}>
                  <GroupIcon className={cn("w-5 h-5", colors.textMuted)} />
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className={cn("font-medium", colors.textPrimary)}>{config.label}</span>
                    {isActiveScope && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-300">
                        Active Scope
                      </Badge>
                    )}
                  </div>
                  <p className={cn("text-xs", colors.textMuted)}>{config.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-xs">
                  {enabledCount} / {supportedCount}
                </Badge>
                {isExpanded ? (
                  <ChevronUp className={cn("w-5 h-5", colors.textMuted)} />
                ) : (
                  <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
                )}
              </div>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 pt-0 space-y-4">
              {/* Quick actions */}
              <div className="flex gap-2 justify-end border-b pb-3 -mt-1" style={{borderColor: 'var(--border)'}}>
                <button 
                  onClick={() => enableAllInScope(scope)}
                  className={cn("text-xs underline hover:text-blue-500", colors.textMuted)}
                >
                  Enable All
                </button>
                <span className={colors.textMuted}>|</span>
                <button 
                  onClick={() => disableAllInScope(scope)}
                  className={cn("text-xs underline hover:text-blue-500", colors.textMuted)}
                >
                  Disable All
                </button>
              </div>
              
              {/* Action list */}
              <div className="space-y-2">
                {actions.map(action => renderActionRow(action))}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };
  
  // Render field restrictions section
  const renderFieldRestrictions = () => {
    const fieldCount = Object.keys(aggregatedFields).length;
    const enabledCount = Object.keys(aggregatedFields).filter(f => isFieldEnabled(f)).length;
    
    // Group fields by source entity type for display
    const fieldsByEntity = {};
    Object.entries(aggregatedFields).forEach(([fieldKey, fieldConfig]) => {
      const entityType = fieldConfig.sourceEntityType;
      if (!fieldsByEntity[entityType]) {
        fieldsByEntity[entityType] = [];
      }
      fieldsByEntity[entityType].push({ fieldKey, ...fieldConfig });
    });
    
    if (fieldCount === 0) {
      return (
        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
          <div className="flex items-center gap-2 text-amber-500">
            <Info className="w-4 h-4" />
            <span className="text-sm">No domains selected - select domains to configure fields</span>
          </div>
        </Card>
      );
    }
    
    return (
      <Card className={cn("overflow-hidden", colors.bgCard, colors.borderPrimary)}>
        <Collapsible open={expandedFields} onOpenChange={setExpandedFields}>
          <CollapsibleTrigger asChild>
            <button className={cn(
              "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg bg-green-500/10 border border-green-500/20")}>
                  <Edit className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-left">
                  <span className={cn("font-medium", colors.textPrimary)}>Editable Fields</span>
                  <p className={cn("text-xs", colors.textMuted)}>
                    Fields the AI can use when creating or modifying items
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-xs">
                  {enabledCount} / {fieldCount}
                </Badge>
                {expandedFields ? (
                  <ChevronUp className={cn("w-5 h-5", colors.textMuted)} />
                ) : (
                  <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
                )}
              </div>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 pt-0 space-y-4">
              {/* Quick actions */}
              <div className="flex gap-2 justify-end border-b pb-3 -mt-1" style={{borderColor: 'var(--border)'}}>
                <button 
                  onClick={() => toggleAllFields(true)}
                  className={cn("text-xs underline hover:text-blue-500", colors.textMuted)}
                >
                  Enable All
                </button>
                <span className={colors.textMuted}>|</span>
                <button 
                  onClick={() => toggleAllFields(false)}
                  className={cn("text-xs underline hover:text-blue-500", colors.textMuted)}
                >
                  Disable All
                </button>
              </div>
              
              {/* Fields grouped by entity type */}
              <div className="space-y-4">
                {Object.entries(fieldsByEntity).map(([entityType, fields]) => (
                  <div key={entityType}>
                    <h4 className={cn("text-xs font-medium uppercase tracking-wide mb-2", colors.textMuted)}>
                      {entityType} Fields
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {fields.map(({ fieldKey, fieldName, label, required }) => {
                        const enabled = isFieldEnabled(fieldKey);
                        return (
                          <div 
                            key={fieldKey}
                            className={cn(
                              "flex items-center gap-2 p-2 rounded-lg border transition-colors",
                              enabled 
                                ? "bg-green-500/5 border-green-500/20"
                                : "bg-gray-500/5 border-transparent"
                            )}
                          >
                            <Switch
                              id={`field-${fieldKey}`}
                              checked={enabled}
                              onCheckedChange={(checked) => toggleField(fieldKey, checked)}
                              className="scale-75"
                            />
                            <Label 
                              htmlFor={`field-${fieldKey}`}
                              className={cn(
                                "text-sm cursor-pointer",
                                enabled ? colors.textPrimary : colors.textMuted
                              )}
                            >
                              {label || fieldName}
                              {required && (
                                <span className="text-red-500 ml-0.5">*</span>
                              )}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };
  
  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className={cn(
        "flex items-start gap-3 p-3 rounded-lg text-sm",
        "bg-blue-500/10 border border-blue-500/20"
      )}>
        <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div>
          <p className={colors.textPrimary}>
            Configure which actions this co-pilot can suggest. 
            {selectedDomains.length > 0 && (
              <>
                {" "}Domains: 
                <span className="font-mono mx-1 text-blue-600 dark:text-blue-400">
                  {selectedDomains.join(", ")}
                </span>
              </>
            )}
          </p>
          <p className={cn("text-xs mt-1", colors.textMuted)}>
            The co-pilot operates in <span className="font-medium">{promptScope}</span> scope.
          </p>
        </div>
      </div>
      
      {/* Item Actions Group */}
      {itemActions.length > 0 && renderActionGroup('item', itemActions)}
      
      {/* Collection Actions Group */}
      {collectionActions.length > 0 && renderActionGroup('collection', collectionActions)}
      
      {/* Field Restrictions */}
      {renderFieldRestrictions()}
    </div>
  );
};

export default UnifiedActionsEditor;
